import { cloneDeep } from 'lodash';
import { SERVICE_LEVELS } from './constants';

/**
 * A service level as the Onward v2 editor lists it.
 * @typedef {Object} ServiceLevelOption
 * @property {string} key - Unique per level: the service_level_id for a custom level, the type prefix otherwise.
 * @property {string} label
 * @property {string} typePrefix - Rate type prefix, e.g. CURBSIDE. Every custom level shares CUSTOM_SERVICE_LEVEL.
 * @property {string} [service_level_id] - Set only for custom levels.
 */

/**
 * The levels the Onward v2 editor offers: the owner's custom levels, then the built-in ones.
 * @param {Array<{ service_level_id: string, service_level: string }>} serviceLevels - The owner's custom levels.
 * @returns {ServiceLevelOption[]}
 */
export const genServiceLevels = (serviceLevels = []) => {
    return [
        ...serviceLevels.map((service_level) => ({
            key: service_level.service_level_id,
            label: service_level.service_level,
            typePrefix: 'CUSTOM_SERVICE_LEVEL',
            service_level_id: service_level.service_level_id,
        })),
        ...Object.entries(SERVICE_LEVELS).map(([key, label]) => ({
            key,
            label,
            typePrefix: key,
        })),
    ];
};

// A custom level prices with the matching built-in level's rates until it has rows of its own (onwardv2
// filterServiceRates), and a $0 row ends that fallback. So rows generated for a custom level are blank:
// the save drops them, and a level nobody fills in keeps falling back.
const blankCustomRows = (serviceLevel, rates) =>
    serviceLevel.service_level_id
        ? Object.fromEntries(
              Object.entries(rates).map(([type, rows]) => [type, rows.map((row) => ({ ...row, rate: null }))])
          )
        : rates;

const levelSubregionRates = (serviceLevel, subregions) => {
    const { typePrefix, service_level_id } = serviceLevel;
    const zoneRows = () =>
        subregions.map((subregion) => ({
            subregion_id: subregion.subregion_id,
            service_level_id,
            min: 0,
            max: 'infinity',
            min2: 0,
            max2: 'infinity',
            rate: 0,
        }));
    return blankCustomRows(serviceLevel, {
        [`${typePrefix}_BASE_RATE`]: zoneRows(),
        [`${typePrefix}_MIN_RATE`]: zoneRows(),
        [`${typePrefix}_ADDITIONAL_PRIMARY_RATE`]: [],
        [`${typePrefix}_ADDITIONAL_SECONDARY_RATE`]: [],
        [`${typePrefix}_ITEMS_INCREMENTAL_RATE`]: zoneRows(),
        [`${typePrefix}_ITEMS_TOTAL_RATE`]: zoneRows(),
        [`${typePrefix}_CABINETS_INCREMENTAL_RATE`]: zoneRows(),
    });
};

const DEFAULT_BANDS = [0, 25, 50, 75, 'infinity'];

const levelDefaultRates = (serviceLevel) => {
    const { typePrefix, service_level_id } = serviceLevel;
    const bandRows = () =>
        DEFAULT_BANDS.slice(0, -1).map((min, idx) => ({
            service_level_id,
            min,
            max: DEFAULT_BANDS[idx + 1],
            min2: 0,
            max2: 'infinity',
        }));
    return blankCustomRows(serviceLevel, {
        [`${typePrefix}_BASE_RATE`]: bandRows(),
        [`${typePrefix}_MIN_RATE`]: bandRows(),
        [`${typePrefix}_ADDITIONAL_PRIMARY_RATE`]: [],
        [`${typePrefix}_ADDITIONAL_SECONDARY_RATE`]: [],
        [`${typePrefix}_ITEMS_INCREMENTAL_RATE`]: bandRows(),
        [`${typePrefix}_ITEMS_TOTAL_RATE`]: bandRows(),
        [`${typePrefix}_CABINETS_INCREMENTAL_RATE`]: bandRows(),
        [`${typePrefix}_LABOR_INCREMENTAL_RATE`]: [{ service_level_id, min: 30, max: 'infinity', rate: 0 }],
        [`${typePrefix}_LABOR_MAX_RATE`]: [{ service_level_id, min: 0, max: 'infinity', rate: 0 }],
        [`${typePrefix}_LABOR_INCREMENTS`]: [{ service_level_id, min: 0, max: 'infinity', rate: 5 }],
    });
};

// Stop Adjustment's one row per level. Kept out of levelDefaultRates, which also runs on an X Axis reset and
// would clear saved Stop Adjustments.
const levelStopAdjustmentRows = (serviceLevel) => {
    const { typePrefix, service_level_id } = serviceLevel;
    return blankCustomRows(serviceLevel, {
        [`${typePrefix}_STOP_ADJUSTMENT`]: [{ service_level_id, min: 0, max: 'infinity' }],
    });
};

// Built-in levels each have their own type prefix, but every custom level writes to the same
// CUSTOM_SERVICE_LEVEL_* types, so the levels' rows are concatenated by type, not overwritten.
const combineLevelRates = (perLevel) =>
    perLevel.reduce((acc, rates) => {
        Object.entries(rates).forEach(([type, rows]) => {
            acc[type] = [...(acc[type] || []), ...rows];
        });
        return acc;
    }, {});

/**
 * Fresh zone grids for every level, for the Area (zip) axis. Built-in levels get $0 rows and custom levels
 * blank ones. Pass every custom level's id to `setRatesMulti` as `serviceLevelIds` so each one is reset.
 * @param {Array<{ subregion_id: string }>} subregions - The selected delivery profile's pricing zones.
 * @param {Array<{ service_level_id: string, service_level: string }>} service_levels - The owner's custom levels.
 * @returns {Object<string, Object[]>} Rows keyed by rate type.
 */
export const genSubregionDefaultRates = (subregions = [], service_levels = []) =>
    combineLevelRates(
        genServiceLevels(service_levels).map((serviceLevel) => levelSubregionRates(serviceLevel, subregions))
    );

/**
 * Fresh band grids and labor rows for every level, for the other axes. Custom levels' rows are blank. Pass
 * every custom level's id to `setRatesMulti` as `serviceLevelIds` so each one is reset.
 * @param {Array<{ service_level_id: string, service_level: string }>} service_levels - The owner's custom levels.
 * @returns {Object<string, Object[]>} Rows keyed by rate type.
 */
export const genDefaultRates = (service_levels = []) =>
    combineLevelRates(genServiceLevels(service_levels).map(levelDefaultRates));

/**
 * A Stop Adjustment row with no rate for each built-in level, for DEFAULT_RATES. A QBO code picked before the
 * rate needs a row to land on. Custom levels get theirs from `missingLevelRates`.
 * @returns {Object<string, Object[]>} Rows keyed by rate type.
 */
export const genStopAdjustmentRates = () => combineLevelRates(genServiceLevels().map(levelStopAdjustmentRows));

// What a custom level needs before it's edited: the Base Rates and Item Rates grids show its zones or bands,
// Labor's "after N minutes" is its row's min, and a QBO code only sticks to a row that exists.
const SCAFFOLD_RATE_TYPES = [
    'BASE_RATE',
    'MIN_RATE',
    'ITEMS_INCREMENTAL_RATE',
    'ITEMS_TOTAL_RATE',
    'CABINETS_INCREMENTAL_RATE',
    'LABOR_INCREMENTAL_RATE',
    'LABOR_MAX_RATE',
    'LABOR_INCREMENTS',
    'STOP_ADJUSTMENT',
];

/**
 * Blank rows for the types a custom level has no rows of, so selecting it shows its zones or bands, Labor
 * starts "after 30" like the built-in levels, and a QBO code picked before a rate is kept. Returns `{}` for a
 * built-in level. On the Area (zip) axis with no zones to build from, the grids are left out.
 * @param {Object<string, Object[]>} rates - The editor's rates, keyed by type.
 * @param {ServiceLevelOption} serviceLevel
 * @param {{ axis?: string, subregions?: Array<{ subregion_id: string }> }} grid - The tariff's X axis and
 *     the selected profile's zones.
 * @returns {Object<string, Object[]>} Rows keyed by rate type.
 */
export const missingLevelRates = (rates, serviceLevel, { axis, subregions = [] } = {}) => {
    if (!serviceLevel?.service_level_id) {
        return {};
    }
    // Labor and Stop Adjustment don't depend on the axis. On Area (zip), the grids are the profile's zones.
    const blank = {
        ...levelDefaultRates(serviceLevel),
        ...(axis === 'SUBREGION' ? levelSubregionRates(serviceLevel, subregions) : {}),
        ...levelStopAdjustmentRows(serviceLevel),
    };
    const hasRows = (type) => (rates[type] || []).some((row) => row.service_level_id === serviceLevel.service_level_id);
    return Object.fromEntries(
        SCAFFOLD_RATE_TYPES.map((suffix) => `${serviceLevel.typePrefix}_${suffix}`)
            .filter((type) => !hasRows(type) && blank[type].length)
            .map((type) => [type, blank[type]])
    );
};

/** Rate type suffixes every service level has its own rows of. Copy Base Rates copies these. */
export const LEVEL_RATE_TYPES = [
    'BASE_RATE',
    'MIN_RATE',
    'ITEMS_INCREMENTAL_RATE',
    'ITEMS_TOTAL_RATE',
    'CABINETS_INCREMENTAL_RATE',
    'ADDITIONAL_PRIMARY_RATE',
    'ADDITIONAL_SECONDARY_RATE',
    'LABOR_INCREMENTAL_RATE',
    'LABOR_MAX_RATE',
    'LABOR_INCREMENTS',
    'ITEM_RATE',
    'STOP_ADJUSTMENT',
];

// The Base Rates grid and the grids drawn against its bands or zones: what Generate New Grid rebuilds. A level's
// rows of these only line up with its own bands, so Copy Base Rates replaces them together.
const GRID_RATE_TYPES = [
    'BASE_RATE',
    'MIN_RATE',
    'ADDITIONAL_PRIMARY_RATE',
    'ADDITIONAL_SECONDARY_RATE',
    'ITEMS_INCREMENTAL_RATE',
    'ITEMS_TOTAL_RATE',
    'CABINETS_INCREMENTAL_RATE',
    'ITEM_RATE',
];

/**
 * Copy Base Rates: copies the source level's rows onto each target level. Pass the result straight to
 * `setRatesMulti`. Its `serviceLevelIds` are the custom targets, so the store replaces their rows in the
 * shared CUSTOM_SERVICE_LEVEL_* types instead of adding to them. If the source has Base Rates saved (a row with
 * a numeric rate), every grid type is copied, as `[]` where the source has no rows, so no target keeps rows on
 * bands the copied grid doesn't have. Otherwise the grid is left out. Labor and Stop Adjustment are left out
 * when the source has nothing saved for them. Targets keep their own rows of whatever is left out.
 * @param {Object<string, Object[]>} rates - The editor's rates, keyed by type.
 * @param {ServiceLevelOption} source
 * @param {ServiceLevelOption[]} targets - The source is skipped if listed.
 * @returns {{ rates: Object<string, Object[]>, serviceLevelIds: string[] }}
 */
export const copyLevelRates = (rates, source, targets) => {
    const levels = targets.filter(({ key }) => key !== source.key);
    const sourceRows = (suffix) =>
        (rates[`${source.typePrefix}_${suffix}`] || []).filter(
            (row) => !source.service_level_id || row.service_level_id === source.service_level_id
        );
    const hasRate = (rows) => rows.some((row) => Number.isFinite(row.rate));
    const copyGrid = hasRate(sourceRows('BASE_RATE'));
    const copied = {};
    LEVEL_RATE_TYPES.forEach((suffix) => {
        const rows = sourceRows(suffix);
        if (!(GRID_RATE_TYPES.includes(suffix) ? copyGrid : hasRate(rows))) {
            return;
        }
        levels.forEach((level) => {
            const type = `${level.typePrefix}_${suffix}`;
            copied[type] = [
                ...(copied[type] || []),
                ...rows.map((row) => cloneDeep({ ...row, type, service_level_id: level.service_level_id })),
            ];
        });
    });
    return {
        rates: copied,
        serviceLevelIds: levels.map((level) => level.service_level_id).filter(Boolean),
    };
};
